/**
 * AxLabs Mathematics Experiments - Probability
 *
 * Collection of experiments exploring probability concepts and distributions.
 */
import { createExperimentFromSchema } from '../../framework/index.js';
import { getParam, getState, binomial } from './helpers.js';
// ============================================================================
// COIN TOSS CONVERGENCE
// ============================================================================
export const CoinTossConvergence = createExperimentFromSchema({
    id: 'coin-toss-convergence',
    name: 'Coin Toss Convergence',
    domain: 'mathematics',
    description: 'Demonstrate the Law of Large Numbers through coin tossing',
    conceptTags: ['Law of Large Numbers', 'Convergence', 'Expected Value', 'Coin Toss'],
    difficulty: 2,
    stateVariables: [
        { id: 'tosses', name: 'Total Tosses', unit: 'dimensionless', initial: 1, symbol: 'n' },
        { id: 'heads', name: 'Heads Count', unit: 'dimensionless', initial: 0, symbol: 'H' },
        { id: 'tails', name: 'Tails Count', unit: 'dimensionless', initial: 1, symbol: 'T' },
        { id: 'headRatio', name: 'Heads Ratio', unit: 'dimensionless', initial: 0, symbol: '\\hat{p}' },
        { id: 'expected', name: 'Expected (0.5)', unit: 'dimensionless', initial: 0.5, symbol: 'p' },
        { id: 'deviation', name: '|ratio - 0.5|', unit: 'dimensionless', initial: 0.5, symbol: '|\\hat{p}-p|' },
    ],
    parameters: [
        { id: 'p', name: 'Bias (Heads prob)', unit: 'dimensionless', min: 0, max: 1, default: 0.5, symbol: 'p' },
        { id: 'tossesPerFrame', name: 'Tosses per Frame', unit: 'dimensionless', min: 1, max: 100, default: 5, symbol: 'k' },
        { id: 'seed', name: 'Random Seed', unit: 'dimensionless', min: 1, max: 100000, default: 12345, symbol: 's' },
    ],
    equations: [
        {
            id: 'law-large-numbers',
            name: 'Law of Large Numbers',
            latex: '\\lim_{n\\to\\infty} \\hat{p}_n = p',
            description: 'Sample proportion converges to true probability',
        },
        {
            id: 'expected-value',
            name: 'Expected Value',
            latex: 'E[H] = np',
            description: 'Expected number of heads after n tosses',
        },
    ],
    derivatives: (state, params) => {
        const p = getParam(params, 'p');
        const k = Math.round(getParam(params, 'tossesPerFrame'));
        const seed = getParam(params, 'seed');
        const tosses = getState(state, 'tosses');
        const heads = getState(state, 'heads');
        const nextTosses = tosses + k;
        // Pseudo-random coin tosses
        let newHeads = 0;
        for (let i = 0; i < k; i++) {
            const x = Math.sin(seed + tosses + i) * 10000;
            const rand = x - Math.floor(x);
            newHeads += rand < p ? 1 : 0;
        }
        const nextHeads = heads + newHeads;
        const _nextTails = nextTosses - nextHeads; // Calculated but unused
        void _nextTails;
        const headRatio = nextHeads / nextTosses;
        const deviation = Math.abs(headRatio - p);
        return {
            tosses: k,
            heads: newHeads,
            tails: (nextTosses - nextHeads) - getState(state, 'tails'),
            headRatio: headRatio - getState(state, 'headRatio'),
            expected: 0,
            deviation: deviation - getState(state, 'deviation'),
        };
    },
    bounds: {
        tosses: { min: 1, max: 100000 },
        heads: { min: 0, max: 100000 },
        headRatio: { min: 0, max: 1 },
    },
});
// ============================================================================
// DICE ROLL DISTRIBUTION
// ============================================================================
export const DiceRollDistribution = createExperimentFromSchema({
    id: 'dice-roll-distribution',
    name: 'Dice Roll Distribution',
    domain: 'mathematics',
    description: 'Explore probability distribution of dice sums',
    conceptTags: ['Probability Distribution', 'Dice', 'Expected Value', 'Uniform Distribution'],
    difficulty: 3,
    stateVariables: [
        { id: 'rolls', name: 'Total Rolls', unit: 'dimensionless', initial: 1, symbol: 'n' },
        { id: 'sum2', name: 'Count of 2', unit: 'dimensionless', initial: 0, symbol: 'n_2' },
        { id: 'sum7', name: 'Count of 7', unit: 'dimensionless', initial: 0, symbol: 'n_7' },
        { id: 'sum12', name: 'Count of 12', unit: 'dimensionless', initial: 0, symbol: 'n_{12}' },
        { id: 'freq2', name: 'Freq of 2', unit: 'dimensionless', initial: 0, symbol: 'f_2' },
        { id: 'freq7', name: 'Freq of 7', unit: 'dimensionless', initial: 0, symbol: 'f_7' },
        { id: 'freq12', name: 'Freq of 12', unit: 'dimensionless', initial: 0, symbol: 'f_{12}' },
    ],
    parameters: [
        { id: 'numDice', name: 'Number of Dice', unit: 'dimensionless', min: 1, max: 5, default: 2, symbol: 'd' },
        { id: 'sides', name: 'Dice Sides', unit: 'dimensionless', min: 4, max: 20, default: 6, symbol: 's' },
        { id: 'rollsPerFrame', name: 'Rolls per Frame', unit: 'dimensionless', min: 1, max: 50, default: 10, symbol: 'k' },
        { id: 'seed', name: 'Random Seed', unit: 'dimensionless', min: 1, max: 100000, default: 54321, symbol: 'seed' },
    ],
    equations: [
        {
            id: 'expected-sum',
            name: 'Expected Sum',
            latex: 'E[S] = d \\cdot \\frac{s+1}{2}',
            description: 'Expected value of sum of d dice with s sides',
        },
        {
            id: 'uniform-single',
            name: 'Single Die Distribution',
            latex: 'P(X=k) = \\frac{1}{s}',
            description: 'Each face has equal probability',
        },
    ],
    derivatives: (state, params) => {
        const d = Math.round(getParam(params, 'numDice'));
        const s = Math.round(getParam(params, 'sides'));
        const k = Math.round(getParam(params, 'rollsPerFrame'));
        const seed = getParam(params, 'seed');
        const rolls = getState(state, 'rolls');
        const nextRolls = rolls + k;
        // Track frequencies
        let count2 = getState(state, 'sum2');
        let count7 = getState(state, 'sum7');
        let count12 = getState(state, 'sum12');
        for (let i = 0; i < k; i++) {
            let sum = 0;
            for (let j = 0; j < d; j++) {
                const x = Math.sin(seed + rolls + i * d + j) * 10000;
                const rand = x - Math.floor(x);
                sum += Math.floor(rand * s) + 1;
            }
            if (sum === 2)
                count2++;
            if (sum === 7)
                count7++;
            if (sum === 12)
                count12++;
        }
        const freq2 = count2 / nextRolls;
        const freq7 = count7 / nextRolls;
        const freq12 = count12 / nextRolls;
        return {
            rolls: k,
            sum2: count2 - getState(state, 'sum2'),
            sum7: count7 - getState(state, 'sum7'),
            sum12: count12 - getState(state, 'sum12'),
            freq2: freq2 - getState(state, 'freq2'),
            freq7: freq7 - getState(state, 'freq7'),
            freq12: freq12 - getState(state, 'freq12'),
        };
    },
    bounds: {
        rolls: { min: 1, max: 100000 },
        sum2: { min: 0, max: 50000 },
        sum7: { min: 0, max: 50000 },
    },
});
// ============================================================================
// BIRTHDAY PROBLEM SIMULATION
// ============================================================================
export const BirthdayProblemSimulation = createExperimentFromSchema({
    id: 'birthday-problem-simulation',
    name: 'Birthday Problem Simulation',
    domain: 'mathematics',
    description: 'Simulate the famous birthday paradox',
    conceptTags: ['Birthday Paradox', 'Probability', 'Combinatorics', 'Pigeonhole Principle'],
    difficulty: 4,
    stateVariables: [
        { id: 'n', name: 'People', unit: 'dimensionless', initial: 1, symbol: 'n' },
        { id: 'matches', name: 'Shared Birthdays', unit: 'dimensionless', initial: 0, symbol: 'M' },
        { id: 'probMatch', name: 'P(match)', unit: 'dimensionless', initial: 0, symbol: 'P(\\text{match})' },
        { id: 'probNoMatch', name: 'P(no match)', unit: 'dimensionless', initial: 1, symbol: 'P(\\text{no match})' },
        { id: 'theoretical', name: 'Theoretical P', unit: 'dimensionless', initial: 0, symbol: 'P_{theory}' },
    ],
    parameters: [
        { id: 'daysPerYear', name: 'Days per Year', unit: 'dimensionless', min: 30, max: 365, default: 365, symbol: 'D' },
        { id: 'peoplePerFrame', name: 'Add per Frame', unit: 'dimensionless', min: 1, max: 10, default: 1, symbol: 'k' },
        { id: 'trials', name: 'Trials per Step', unit: 'dimensionless', min: 1, max: 1000, default: 100, symbol: 'T' },
        { id: 'seed', name: 'Random Seed', unit: 'dimensionless', min: 1, max: 100000, default: 11111, symbol: 's' },
    ],
    equations: [
        {
            id: 'birthday-prob',
            name: 'Probability of Shared Birthday',
            latex: 'P(\\text{match}) = 1 - \\frac{365!}{365^n(365-n)!}',
            description: 'Probability at least two share a birthday among n people',
        },
        {
            id: 'approximation',
            name: 'Approximation',
            latex: 'P(\\text{match}) \\approx 1 - e^{-n(n-1)/(2 \\cdot 365)}',
            description: 'Exponential approximation for large n',
        },
    ],
    derivatives: (state, params) => {
        const D = Math.round(getParam(params, 'daysPerYear'));
        const k = Math.round(getParam(params, 'peoplePerFrame'));
        const trials = Math.round(getParam(params, 'trials'));
        const seed = getParam(params, 'seed');
        const n = getState(state, 'n');
        const nextN = Math.min(n + k, D);
        // Run Monte Carlo trials
        let matchCount = 0;
        for (let t = 0; t < trials; t++) {
            const birthdays = new Set();
            let hasMatch = false;
            for (let i = 0; i < nextN; i++) {
                const x = Math.sin(seed + t * 1000 + i) * 10000;
                const rand = x - Math.floor(x);
                const birthday = Math.floor(rand * D);
                if (birthdays.has(birthday)) {
                    hasMatch = true;
                    break;
                }
                birthdays.add(birthday);
            }
            if (hasMatch)
                matchCount++;
        }
        const probMatch = matchCount / trials;
        const probNoMatch = 1 - probMatch;
        // Theoretical probability
        let theoreticalNoMatch = 1;
        for (let i = 0; i < nextN; i++) {
            theoreticalNoMatch *= (D - i) / D;
        }
        const theoretical = 1 - theoreticalNoMatch;
        return {
            n: nextN - n,
            matches: probMatch > 0.5 ? 1 : 0,
            probMatch: probMatch - getState(state, 'probMatch'),
            probNoMatch: probNoMatch - getState(state, 'probNoMatch'),
            theoretical: theoretical - getState(state, 'theoretical'),
        };
    },
    bounds: {
        n: { min: 1, max: 365 },
        probMatch: { min: 0, max: 1 },
    },
});
// ============================================================================
// CONDITIONAL PROBABILITY EXPLORER
// ============================================================================
export const ConditionalProbabilityExplorer = createExperimentFromSchema({
    id: 'conditional-probability-explorer',
    name: 'Conditional Probability Explorer',
    domain: 'mathematics',
    description: 'Explore conditional probability with visual diagrams',
    conceptTags: ['Conditional Probability', 'Bayes Theorem', 'Independence', 'Venn Diagrams'],
    difficulty: 5,
    stateVariables: [
        { id: 'total', name: 'Total Outcomes', unit: 'dimensionless', initial: 100, symbol: 'N' },
        { id: 'eventA', name: 'Event A Count', unit: 'dimensionless', initial: 30, symbol: 'n(A)' },
        { id: 'eventB', name: 'Event B Count', unit: 'dimensionless', initial: 40, symbol: 'n(B)' },
        { id: 'intersection', name: 'A ∩ B Count', unit: 'dimensionless', initial: 12, symbol: 'n(A \\cap B)' },
        { id: 'probA', name: 'P(A)', unit: 'dimensionless', initial: 0.3, symbol: 'P(A)' },
        { id: 'probB', name: 'P(B)', unit: 'dimensionless', initial: 0.4, symbol: 'P(B)' },
        { id: 'probAGivenB', name: 'P(A|B)', unit: 'dimensionless', initial: 0.3, symbol: 'P(A|B)' },
        { id: 'probBGivenA', name: 'P(B|A)', unit: 'dimensionless', initial: 0.4, symbol: 'P(B|A)' },
    ],
    parameters: [
        { id: 'probA_target', name: 'P(A) Target', unit: 'dimensionless', min: 0, max: 1, default: 0.3, symbol: 'P(A)' },
        { id: 'probB_target', name: 'P(B) Target', unit: 'dimensionless', min: 0, max: 1, default: 0.4, symbol: 'P(B)' },
        { id: 'correlation', name: 'Correlation', unit: 'dimensionless', min: -1, max: 1, default: 0, symbol: '\\rho' },
    ],
    equations: [
        {
            id: 'conditional-prob',
            name: 'Conditional Probability Definition',
            latex: 'P(A|B) = \\frac{P(A \\cap B)}{P(B)}',
            description: 'Probability of A given B has occurred',
        },
        {
            id: 'bayes-theorem',
            name: 'Bayes\' Theorem',
            latex: 'P(A|B) = \\frac{P(B|A)P(A)}{P(B)}',
            description: 'Relates conditional probabilities',
        },
        {
            id: 'independence',
            name: 'Independence',
            latex: 'P(A \\cap B) = P(A)P(B) \\iff A \\perp B',
            description: 'Events are independent if joint equals product',
        },
    ],
    derivatives: (_state, params) => {
        const probA_target = getParam(params, 'probA_target');
        const probB_target = getParam(params, 'probB_target');
        const correlation = getParam(params, 'correlation');
        const total = 100;
        const eventA = Math.round(probA_target * total);
        const eventB = Math.round(probB_target * total);
        // Calculate intersection based on correlation
        // correlation = 1: maximum overlap, correlation = -1: minimum overlap
        const maxIntersection = Math.min(eventA, eventB);
        const minIntersection = Math.max(0, eventA + eventB - total);
        const intersection = Math.round(minIntersection + (maxIntersection - minIntersection) * (correlation + 1) / 2);
        const probA = eventA / total;
        const probB = eventB / total;
        const probIntersection = intersection / total;
        const probA_given_B = probB > 0 ? probIntersection / probB : 0;
        const probB_given_A = probA > 0 ? probIntersection / probA : 0;
        return {
            total: 0,
            eventA: eventA - getState(_state, 'eventA'),
            eventB: eventB - getState(_state, 'eventB'),
            intersection: intersection - getState(_state, 'intersection'),
            probA: probA - getState(_state, 'probA'),
            probB: probB - getState(_state, 'probB'),
            probAGivenB: probA_given_B - getState(_state, 'probAGivenB'),
            probBGivenA: probB_given_A - getState(_state, 'probBGivenA'),
        };
    },
    bounds: {
        total: { min: 0, max: 100 },
        eventA: { min: 0, max: 100 },
        eventB: { min: 0, max: 100 },
        intersection: { min: 0, max: 100 },
    },
});
// ============================================================================
// BINOMIAL DISTRIBUTION SHAPE
// ============================================================================
export const BinomialDistributionShape = createExperimentFromSchema({
    id: 'binomial-distribution-shape',
    name: 'Binomial Distribution Shape',
    domain: 'mathematics',
    description: 'Explore how n and p affect binomial distribution shape',
    conceptTags: ['Binomial Distribution', 'Discrete Probability', 'Expected Value', 'Variance'],
    difficulty: 4,
    stateVariables: [
        { id: 'k', name: 'Successes k', unit: 'dimensionless', initial: 0, symbol: 'k' },
        { id: 'prob', name: 'P(X=k)', unit: 'dimensionless', initial: 0.5, symbol: 'P(X=k)' },
        { id: 'expected', name: 'Expected E[X]', unit: 'dimensionless', initial: 10, symbol: 'E[X]' },
        { id: 'variance', name: 'Variance Var[X]', unit: 'dimensionless', initial: 5, symbol: 'Var[X]' },
        { id: 'stdDev', name: 'Std Dev σ', unit: 'dimensionless', initial: 2.24, symbol: '\\sigma' },
        { id: 'mode', name: 'Mode', unit: 'dimensionless', initial: 10, symbol: 'Mode' },
    ],
    parameters: [
        { id: 'n', name: 'Trials n', unit: 'dimensionless', min: 1, max: 100, default: 20, symbol: 'n' },
        { id: 'p', name: 'Success prob p', unit: 'dimensionless', min: 0.01, max: 0.99, default: 0.5, symbol: 'p' },
        { id: 'scanK', name: 'Scan k', unit: 'dimensionless', min: 0, max: 1, default: 0.5, symbol: 'k_{scan}' },
    ],
    equations: [
        {
            id: 'binomial-pmf',
            name: 'Binomial PMF',
            latex: 'P(X=k) = \\binom{n}{k}p^k(1-p)^{n-k}',
            description: 'Probability of exactly k successes in n trials',
        },
        {
            id: 'binomial-mean',
            name: 'Expected Value',
            latex: 'E[X] = np',
            description: 'Mean of binomial distribution',
        },
        {
            id: 'binomial-variance',
            name: 'Variance',
            latex: 'Var[X] = np(1-p)',
            description: 'Variance of binomial distribution',
        },
    ],
    derivatives: (state, params) => {
        const n = Math.round(getParam(params, 'n'));
        const p = getParam(params, 'p');
        const scanK = getParam(params, 'scanK');
        const k = Math.floor(scanK * n);
        const prob = binomial(n, k) * Math.pow(p, k) * Math.pow(1 - p, n - k);
        const expected = n * p;
        const variance = n * p * (1 - p);
        const stdDev = Math.sqrt(variance);
        const mode = Math.floor((n + 1) * p);
        return {
            k: k - getState(state, 'k'),
            prob: prob - getState(state, 'prob'),
            expected: expected - getState(state, 'expected'),
            variance: variance - getState(state, 'variance'),
            stdDev: stdDev - getState(state, 'stdDev'),
            mode: mode - getState(state, 'mode'),
        };
    },
    bounds: {
        k: { min: 0, max: 100 },
        prob: { min: 0, max: 0.5 },
        expected: { min: 0, max: 100 },
    },
});
// ============================================================================
// GEOMETRIC DISTRIBUTION BEHAVIOR
// ============================================================================
export const GeometricDistributionBehavior = createExperimentFromSchema({
    id: 'geometric-distribution-behavior',
    name: 'Geometric Distribution Behavior',
    domain: 'mathematics',
    description: 'Explore the geometric distribution of trials until first success',
    conceptTags: ['Geometric Distribution', 'Memoryless Property', 'Expected Value', 'Discrete'],
    difficulty: 4,
    stateVariables: [
        { id: 'k', name: 'Trial k', unit: 'dimensionless', initial: 1, symbol: 'k' },
        { id: 'pmf', name: 'P(X=k)', unit: 'dimensionless', initial: 0.1, symbol: 'P(X=k)' },
        { id: 'cdf', name: 'P(X≤k)', unit: 'dimensionless', initial: 0.1, symbol: 'F(k)' },
        { id: 'expected', name: 'Expected E[X]', unit: 'dimensionless', initial: 10, symbol: 'E[X]' },
        { id: 'variance', name: 'Variance Var[X]', unit: 'dimensionless', initial: 90, symbol: 'Var[X]' },
    ],
    parameters: [
        { id: 'p', name: 'Success prob p', unit: 'dimensionless', min: 0.01, max: 0.5, default: 0.1, symbol: 'p' },
        { id: 'maxK', name: 'Max k to display', unit: 'dimensionless', min: 10, max: 100, default: 50, symbol: 'k_{max}' },
        { id: 'scanK', name: 'Scan position', unit: 'dimensionless', min: 0, max: 1, default: 0, symbol: 's' },
    ],
    equations: [
        {
            id: 'geometric-pmf',
            name: 'Geometric PMF',
            latex: 'P(X=k) = (1-p)^{k-1}p',
            description: 'Probability first success occurs on trial k',
        },
        {
            id: 'geometric-cdf',
            name: 'Geometric CDF',
            latex: 'P(X \\leq k) = 1 - (1-p)^k',
            description: 'Probability success occurs by trial k',
        },
        {
            id: 'geometric-mean',
            name: 'Expected Value',
            latex: 'E[X] = \\frac{1}{p}',
            description: 'Average trials until first success',
        },
        {
            id: 'memoryless',
            name: 'Memoryless Property',
            latex: 'P(X > k+n | X > k) = P(X > n)',
            description: 'Past failures don\'t affect future probability',
        },
    ],
    derivatives: (state, params) => {
        const p = getParam(params, 'p');
        const maxK = Math.round(getParam(params, 'maxK'));
        const scanK = getParam(params, 'scanK');
        const k = Math.floor(scanK * maxK) + 1;
        const q = 1 - p;
        const pmf = Math.pow(q, k - 1) * p;
        const cdf = 1 - Math.pow(q, k);
        const expected = 1 / p;
        const variance = q / (p * p);
        return {
            k: k - getState(state, 'k'),
            pmf: pmf - getState(state, 'pmf'),
            cdf: cdf - getState(state, 'cdf'),
            expected: expected - getState(state, 'expected'),
            variance: variance - getState(state, 'variance'),
        };
    },
    bounds: {
        k: { min: 1, max: 100 },
        pmf: { min: 0, max: 1 },
        expected: { min: 2, max: 100 },
    },
});
// ============================================================================
// MONTE CARLO ESTIMATION
// ============================================================================
export const MonteCarloEstimation = createExperimentFromSchema({
    id: 'monte-carlo-estimation',
    name: 'Monte Carlo Estimation',
    domain: 'mathematics',
    description: 'Use Monte Carlo methods to estimate π and other values',
    conceptTags: ['Monte Carlo', 'Pi Estimation', 'Random Sampling', 'Convergence'],
    difficulty: 3,
    stateVariables: [
        { id: 'samples', name: 'Total Samples', unit: 'dimensionless', initial: 1, symbol: 'N' },
        { id: 'inside', name: 'Points Inside', unit: 'dimensionless', initial: 1, symbol: 'N_{in}' },
        { id: 'ratio', name: 'Inside Ratio', unit: 'dimensionless', initial: 1, symbol: 'N_{in}/N' },
        { id: 'piEstimate', name: 'π Estimate', unit: 'dimensionless', initial: 4, symbol: '\\hat{\\pi}' },
        { id: 'error', name: 'Error', unit: 'dimensionless', initial: 0.86, symbol: '|\\hat{\\pi}-\\pi|' },
    ],
    parameters: [
        { id: 'samplesPerFrame', name: 'Samples per Frame', unit: 'dimensionless', min: 10, max: 1000, default: 100, symbol: 'k' },
        { id: 'seed', name: 'Random Seed', unit: 'dimensionless', min: 1, max: 100000, default: 7777, symbol: 's' },
    ],
    equations: [
        {
            id: 'monte-carlo-pi',
            name: 'Monte Carlo π Estimation',
            latex: '\\pi \\approx 4 \\cdot \\frac{N_{in}}{N}',
            description: 'Estimate π by sampling points in unit square',
        },
        {
            id: 'convergence-rate',
            name: 'Convergence Rate',
            latex: '\\text{Error} \\propto \\frac{1}{\\sqrt{N}}',
            description: 'Monte Carlo converges at rate 1/√N',
        },
    ],
    derivatives: (state, params) => {
        const k = Math.round(getParam(params, 'samplesPerFrame'));
        const seed = getParam(params, 'seed');
        const samples = getState(state, 'samples');
        const inside = getState(state, 'inside');
        const nextSamples = samples + k;
        let newInside = 0;
        for (let i = 0; i < k; i++) {
            const x1 = Math.sin(seed + samples + i * 2) * 10000;
            const x2 = Math.cos(seed + samples + i * 2) * 10000;
            const rx = (x1 - Math.floor(x1)) * 2 - 1;
            const ry = (x2 - Math.floor(x2)) * 2 - 1;
            if (rx * rx + ry * ry <= 1) {
                newInside++;
            }
        }
        const nextInside = inside + newInside;
        const ratio = nextInside / nextSamples;
        const piEstimate = 4 * ratio;
        const error = Math.abs(piEstimate - Math.PI);
        return {
            samples: k,
            inside: newInside,
            ratio: ratio - getState(state, 'ratio'),
            piEstimate: piEstimate - getState(state, 'piEstimate'),
            error: error - getState(state, 'error'),
        };
    },
    bounds: {
        samples: { min: 1, max: 1000000 },
        inside: { min: 0, max: 1000000 },
        piEstimate: { min: 2, max: 4.5 },
    },
});
//# sourceMappingURL=probability.js.map